import type { Artboard, File, RiveCanvas, StateMachineInstance, ViewModelInstance, WrappedRenderer } from "@rive-app/webgl2-advanced";
import { CanvasSource, Texture } from "pixi.js";
import type { Log } from "@slot/presentation";
import { riveRuntime } from "./riveRuntime";

/** A Rive file's artboard, the state machine that plays it and the view model instance that holds its inputs. */
export interface RiveArtboardSpec {
  url: string;
  artboard: string;
  stateMachine: string;
  viewModel: string;
  viewModelInstance: string;
}

/** What one loaded artboard holds on to: the runtime's objects, freed on dispose. */
interface RiveArtboardParts {
  rive: RiveCanvas;
  file: File;
  artboard: Artboard;
  machine: StateMachineInstance;
  viewModel: ViewModelInstance;
  renderer: WrappedRenderer;
  canvas: HTMLCanvasElement;
}

/**
 * One Rive artboard drawn into a Pixi texture, for art that stands among the scene's layers (the chef, behind the ladder
 * bar). The page's runtime plays the artboard's state machine: `render` advances it by the seconds it is given — the
 * caller's engine time — and draws the frame into an offscreen 2D canvas, the texture's source (Rive renders on its own
 * shared WebGL2 context and copies the frame in). The state machine's inputs are its view model instance's properties.
 */
export class RiveArtboard {
  public readonly texture: Texture;
  private readonly source: CanvasSource;

  private constructor(private readonly parts: RiveArtboardParts) {
    this.source = new CanvasSource({ resource: parts.canvas, resolution: 1 });
    this.texture = new Texture({ source: this.source });
  }

  /** The artboard loaded and bound to its view model instance; null, logged, when the runtime, the file or a name is missing. */
  public static async load(spec: RiveArtboardSpec, log: Log): Promise<RiveArtboard | null> {
    try {
      const [rive, bytes] = await Promise.all([riveRuntime(), fetchBytes(spec.url)]);
      const file = await rive.load(bytes, undefined, false);
      const artboard = present(file.artboardByName(spec.artboard), `artboard "${spec.artboard}"`);
      const stateMachine = present(artboard.stateMachineByName(spec.stateMachine), `state machine "${spec.stateMachine}"`);
      const viewModel = present(present(file.viewModelByName(spec.viewModel), `view model "${spec.viewModel}"`).instanceByName(spec.viewModelInstance), `view model instance "${spec.viewModelInstance}"`);
      const machine = new rive.StateMachineInstance(stateMachine, artboard);
      machine.bindViewModelInstance(viewModel);
      const canvas = document.createElement("canvas");
      return new RiveArtboard({ rive, file, artboard, machine, viewModel, renderer: rive.makeRenderer(canvas, true), canvas });
    } catch (error) {
      log(`[RiveArtboard] "${spec.url}" not loaded: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
  }

  /** Draws at `width` × `height` canvas pixels from the next frame on; the texture follows. */
  public resize(width: number, height: number): void {
    this.source.resize(Math.max(1, Math.round(width)), Math.max(1, Math.round(height)), 1);
  }

  /** Fires a trigger of the view model instance (an unknown name is ignored). */
  public trigger(name: string): void {
    this.parts.viewModel.trigger(name)?.trigger();
  }

  /** Sets a boolean of the view model instance (an unknown name is ignored). */
  public setBoolean(name: string, value: boolean): void {
    const property = this.parts.viewModel.boolean(name);
    if (property) property.value = value;
  }

  /** Advances the state machine `deltaSeconds` and draws the frame into the texture. */
  public render(deltaSeconds: number): void {
    const { rive, artboard, machine, renderer, canvas } = this.parts;
    machine.advanceAndApply(deltaSeconds);
    renderer.clear();
    renderer.save();
    renderer.align(rive.Fit.contain, rive.Alignment.center, { minX: 0, minY: 0, maxX: canvas.width, maxY: canvas.height }, artboard.bounds);
    artboard.draw(renderer);
    renderer.restore();
    renderer.flush();
    rive.resolveAnimationFrame();
    this.source.update();
  }

  public dispose(): void {
    const { machine, viewModel, artboard, renderer, file } = this.parts;
    machine.delete();
    viewModel.delete();
    artboard.delete();
    renderer.delete();
    file.unref();
    this.texture.destroy(true);
  }
}

/** A file's bytes; an HTTP error is a failed load. */
export async function fetchBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return new Uint8Array(await response.arrayBuffer());
}

/** The runtime answers a missing name with null: a named error instead. */
function present<T>(value: T | null | undefined, what: string): T {
  if (value === null || value === undefined) throw new Error(`no ${what}`);
  return value;
}
